import { isGuardLeaveMode } from '@/config/guardPortal.mjs'

const ADMIN_SECTION_LABELS = new Set([
  '工作台',
  '设备管理',
  '用户管理',
  '权限中心',
  '班级管理',
  '请假管理',
  '德育管理',
  '家校互通',
  '教学管理',
  '通知与内容',
  '宿舍管理',
  '数据与配置'
])

const TEACHER_SECTION_LABEL_MAP = {
  教师工作台: '工作台',
  教师班级管理: '班级管理',
  教师学生事务: '学生事务',
  教师德育管理: '德育管理',
  教师教学教务: '教学教务',
  教师宿舍服务: '宿舍服务'
}

const DORM_SECTION_LABELS = new Set(['宿管端导航'])
const STUDENT_COUNCIL_SECTION_LABELS = new Set(['德育管理'])
const STUDENT_COUNCIL_ALLOWED_PATHS = new Set(['/student-union/moral', '/moral/score'])
const STUDENT_COUNCIL_PATH_MAP = {
  '/moral/score': '/student-union/moral'
}
const GUARD_SECTION_LABELS = new Set(['德育管理', '请假管理'])
const GUARD_ALLOWED_PATHS = new Set(['/guard/moral', '/guard/records', '/moral/score', '/moral/records', '/leave'])
const GUARD_PATH_MAP = {
  '/moral/score': '/guard/moral',
  '/moral/records': '/guard/records'
}

const ADMIN_PATH_SECTION_OVERRIDES = {
  '/admin/center': '权限中心'
}

function normalizeText(value) {
  return String(value ?? '').trim()
}

function getSectionLabel(section = {}) {
  return normalizeText(section.label ?? section.title)
}

function getSectionItems(section = {}) {
  if (Array.isArray(section.items)) return section.items
  if (Array.isArray(section.children)) return section.children
  return []
}

function normalizeRawItem(item = {}) {
  const path = normalizeText(item.path ?? item.menu_path ?? item.menuPath)
  const name = normalizeText(item.name ?? item.menu_name ?? item.menuName ?? item.label ?? item.title)
  return {
    ...item,
    ...(name ? { name } : {}),
    ...(path ? { path } : {})
  }
}

function normalizeRawSections(rawSections = []) {
  return rawSections.map((section) => ({
    ...section,
    label: getSectionLabel(section),
    items: getSectionItems(section)
      .map(normalizeRawItem)
      .filter(item => item?.path)
  }))
}

function cloneItems(items = []) {
  return items.map(item => ({ ...item }))
}

function cloneSections(sections = []) {
  return sections.map(section => ({
    ...section,
    items: cloneItems(section.items)
  }))
}

function cloneFilteredSections(sections = [], options = {}) {
  const { isHeadTeacher = true } = options
  return cloneSections(sections)
    .map((section) => {
      const items = (section.items || []).filter((item) => {
        if (!item?.path) return false
        if (item.headTeacherOnly && !isHeadTeacher) return false
        return true
      })
      return {
        ...section,
        items
      }
    })
    .filter(section => section.items.length)
}

function buildItemMetaMap(sections = []) {
  const map = new Map()
  sections.forEach((section) => {
    (section.items || []).forEach((item) => {
      if (!item?.path || map.has(item.path)) return
      map.set(item.path, item)
    })
  })
  return map
}

function buildSectionMetaMap(sections = []) {
  const map = new Map()
  sections.forEach((section) => {
    if (!section?.label || map.has(section.label)) return
    map.set(section.label, section)
  })
  return map
}

function enrichItems(items = [], itemMetaMap) {
  return items
    .filter(item => item?.path)
    .map((item) => {
      const meta = itemMetaMap.get(item.path) || {}
      return {
        ...meta,
        ...item
      }
    })
}

function enrichSections(sections = [], fallbackSections = [], options = {}) {
  const { renameLabel } = options
  const itemMetaMap = buildItemMetaMap(fallbackSections)
  const sectionMetaMap = buildSectionMetaMap(fallbackSections)
  return sections
    .map((section) => {
      const label = renameLabel ? renameLabel(section.label) : section.label
      const meta = sectionMetaMap.get(label) || {}
      return {
        ...meta,
        ...section,
        label,
        branchDepth: section.branchDepth || meta.branchDepth || 'short',
        items: enrichItems(section.items, itemMetaMap)
      }
    })
    .filter(section => section.items.length)
}

function ensureTeacherHomeItem(sections = [], fallbackSections = []) {
  const hasHome = sections.some(section => (section.items || []).some(item => item.path === '/'))
  if (hasHome) return sections
  const homeItem = (fallbackSections[0]?.items || []).find(item => item.path === '/')
  if (!homeItem) return sections
  const targetIndex = sections.findIndex(section => section.label === '工作台')
  if (targetIndex === -1) return sections
  const next = cloneSections(sections)
  next[targetIndex] = {
    ...next[targetIndex],
    items: [{ ...homeItem }, ...next[targetIndex].items]
  }
  return next
}

function pickAdminSections(rawSections = [], fallbackSections = []) {
  const fallback = cloneSections(fallbackSections)
  const sectionMetaMap = buildSectionMetaMap(fallback)
  const itemMetaMap = buildItemMetaMap(fallback)
  const sectionBuckets = new Map(
    fallback.map(section => [section.label, []])
  )

  rawSections.forEach((section) => {
    const baseLabel = ADMIN_SECTION_LABELS.has(section.label) ? section.label : ''
    ;(section.items || []).forEach((item) => {
      if (!item?.path) return
      const targetLabel = ADMIN_PATH_SECTION_OVERRIDES[item.path] || baseLabel
      if (!targetLabel || !sectionBuckets.has(targetLabel)) return
      sectionBuckets.get(targetLabel).push(item)
    })
  })

  const sections = fallback
    .map((section) => {
      const items = enrichItems(sectionBuckets.get(section.label) || [], itemMetaMap)
      return items.length
        ? {
            ...sectionMetaMap.get(section.label),
            items
          }
        : null
    })
    .filter(Boolean)

  return sections.length ? sections : fallback
}

function pickAdminSectionsWithoutFallback(rawSections = [], fallbackSections = []) {
  const fallback = cloneSections(fallbackSections)
  const sectionMetaMap = buildSectionMetaMap(fallback)
  const itemMetaMap = buildItemMetaMap(fallback)
  const sectionBuckets = new Map(
    fallback.map(section => [section.label, []])
  )

  rawSections.forEach((section) => {
    const baseLabel = ADMIN_SECTION_LABELS.has(section.label) ? section.label : ''
    ;(section.items || []).forEach((item) => {
      if (!item || item.path == null || item.path === '') return
      const targetLabel = ADMIN_PATH_SECTION_OVERRIDES[item.path] || baseLabel
      if (!targetLabel || !sectionBuckets.has(targetLabel)) return
      sectionBuckets.get(targetLabel).push(item)
    })
  })

  return fallback
    .map((section) => {
      const items = enrichItems(sectionBuckets.get(section.label) || [], itemMetaMap)
      return items.length
        ? {
            ...sectionMetaMap.get(section.label),
            items
          }
        : null
    })
    .filter(Boolean)
}

function pickTeacherSections(rawSections = [], fallbackSections = []) {
  const matched = rawSections.filter((section) => {
    if (TEACHER_SECTION_LABEL_MAP[section.label]) return true
    return (section.items || []).some(item => String(item?.path || '').startsWith('/teacher/'))
  })
  if (!matched.length) return cloneSections(fallbackSections)
  const sections = enrichSections(matched, fallbackSections, {
    renameLabel: label => TEACHER_SECTION_LABEL_MAP[label] || label
  })
  return ensureTeacherHomeItem(sections, fallbackSections)
}

function pickDormSections(rawSections = [], fallbackSections = []) {
  const matched = rawSections.filter((section) => {
    if (DORM_SECTION_LABELS.has(section.label)) return true
    return (section.items || []).some(item => String(item?.path || '').startsWith('/dorm/'))
  })
  if (!matched.length) return cloneSections(fallbackSections)
  return enrichSections(matched, fallbackSections)
}

function normalizeStudentCouncilItem(item = {}) {
  const rawPath = String(item?.path || '')
  const path = STUDENT_COUNCIL_PATH_MAP[rawPath] || rawPath
  if (!STUDENT_COUNCIL_ALLOWED_PATHS.has(rawPath) && !STUDENT_COUNCIL_ALLOWED_PATHS.has(path)) return null
  return {
    ...item,
    path,
    name: path === '/student-union/moral' ? '学生会扣分' : item.name
  }
}

function pickStudentCouncilSections(rawSections = [], fallbackSections = []) {
  const matched = rawSections
    .map((section) => ({
      ...section,
      label: STUDENT_COUNCIL_SECTION_LABELS.has(section.label) ? section.label : '德育管理',
      items: (section.items || []).map(normalizeStudentCouncilItem).filter(Boolean)
    }))
    .filter(section => section.items.length)

  if (!matched.length) return cloneSections(fallbackSections)
  return enrichSections(matched, fallbackSections)
}

function normalizeGuardItem(item = {}) {
  const rawPath = String(item?.path || '')
  const path = GUARD_PATH_MAP[rawPath] || rawPath
  if (path === '/leave' && !isGuardLeaveMode()) return null
  if (path !== '/leave' && isGuardLeaveMode()) return null
  if (!GUARD_ALLOWED_PATHS.has(rawPath) && !GUARD_ALLOWED_PATHS.has(path)) return null
  return {
    ...item,
    path,
    name: path === '/leave'
      ? '请假审批'
      : (path === '/guard/moral'
          ? '门卫扣分'
          : (path === '/guard/records' ? '德育记录' : item.name))
  }
}

function pickGuardSections(rawSections = [], fallbackSections = []) {
  const matched = rawSections
    .map((section) => ({
      ...section,
      label: GUARD_SECTION_LABELS.has(section.label) ? section.label : (isGuardLeaveMode() ? '请假管理' : '德育管理'),
      items: (section.items || []).map(normalizeGuardItem).filter(Boolean)
    }))
    .filter(section => section.items.length)

  if (!matched.length) return cloneSections(fallbackSections)
  return enrichSections(matched, fallbackSections)
}

function filterTeacherSectionsForRole(sections = [], options = {}) {
  const { isHeadTeacher = true } = options
  return cloneFilteredSections(sections, { isHeadTeacher })
}

function normalizeBuiltSections(sections = [], portal = '', options = {}) {
  if (portal === 'teacher') {
    return filterTeacherSectionsForRole(sections, options)
  }
  return cloneSections(sections)
}

function shouldUseFallbackSections(rawSections = [], fallbackMode = 'full') {
  if (fallbackMode === 'none') return false
  if (fallbackMode === 'full') return true
  return Array.isArray(rawSections) && rawSections.length > 0
}

function getBuiltPortalSections(rawSections = [], portal = '', options = {}) {
  const {
    adminSections = [],
    teacherSections = [],
    dormSections = []
  } = options

  const studentCouncilSections = options.studentCouncilSections || []
  const guardSections = options.guardSections || []
  const fallbackMode = options.fallbackMode || 'full'
  const normalizedRawSections = normalizeRawSections(rawSections)
  const pickFallback = shouldUseFallbackSections(normalizedRawSections, fallbackMode)

  if (portal === 'admin') {
    return pickFallback
      ? pickAdminSections(normalizedRawSections, adminSections)
      : pickAdminSectionsWithoutFallback(normalizedRawSections, adminSections)
  }
  if (portal === 'teacher') {
    const built = pickFallback
      ? pickTeacherSections(normalizedRawSections, teacherSections)
      : enrichSections(
          normalizedRawSections.filter((section) => {
            if (TEACHER_SECTION_LABEL_MAP[section.label]) return true
            return (section.items || []).some(item => String(item?.path || '').startsWith('/teacher/'))
          }),
          teacherSections,
          { renameLabel: label => TEACHER_SECTION_LABEL_MAP[label] || label }
        )
    return normalizeBuiltSections(built, portal, options)
  }
  if (portal === 'dorm') {
    return pickFallback
      ? pickDormSections(normalizedRawSections, dormSections)
      : enrichSections(
          normalizedRawSections.filter((section) => {
            if (DORM_SECTION_LABELS.has(section.label)) return true
            return (section.items || []).some(item => String(item?.path || '').startsWith('/dorm/'))
          }),
          dormSections
        )
  }
  if (portal === 'studentcouncil') {
    return pickFallback
      ? pickStudentCouncilSections(normalizedRawSections, studentCouncilSections)
      : enrichSections(
          normalizedRawSections
        .map((section) => ({
          ...section,
          label: STUDENT_COUNCIL_SECTION_LABELS.has(section.label) ? section.label : '德育管理',
          items: (section.items || []).map(normalizeStudentCouncilItem).filter(Boolean)
        }))
        .filter(section => section.items.length),
          studentCouncilSections
        )
  }
  if (portal === 'guard') {
    return pickFallback
      ? pickGuardSections(normalizedRawSections, guardSections)
      : enrichSections(
          normalizedRawSections
        .map((section) => ({
          ...section,
          label: GUARD_SECTION_LABELS.has(section.label) ? section.label : (isGuardLeaveMode() ? '请假管理' : '德育管理'),
          items: (section.items || []).map(normalizeGuardItem).filter(Boolean)
        }))
        .filter(section => section.items.length),
          guardSections
        )
  }
  return []
}

export function getAccessiblePortalSections(rawSections = [], portal = '', options = {}) {
  return getBuiltPortalSections(rawSections, portal, options)
}

export function flattenPortalPaths(sections = []) {
  return sections.flatMap(section => (section.items || []).map(item => item.path).filter(Boolean))
}

export function isPathAllowedBySections(path = '', sections = []) {
  const currentPath = String(path || '').trim()
  if (!currentPath) return false
  const items = sections.flatMap(section => section.items || [])
  return items.some((item) => {
    const itemPath = String(item?.path || '').trim()
    if (!itemPath) return false
    const excludePaths = Array.isArray(item?.excludePaths) ? item.excludePaths : []
    const matchPaths = Array.isArray(item?.matchPaths) ? item.matchPaths : []
    const matchPrefixes = Array.isArray(item?.matchPrefixes) ? item.matchPrefixes : []
    const exact = !!item?.exact
    if (excludePaths.some(excludePath => currentPath === excludePath || currentPath.startsWith(`${excludePath}/`))) {
      return false
    }
    if (matchPaths.includes(currentPath)) return true
    if (matchPrefixes.some(prefix => currentPath === prefix || currentPath.startsWith(`${prefix}/`))) return true
    if (itemPath === '/') return currentPath === '/'
    if (exact) return currentPath === itemPath
    return currentPath === itemPath || currentPath.startsWith(`${itemPath}/`)
  })
}

export function buildPortalNavigation(rawSections = [], portal = '', options = {}) {
  return getBuiltPortalSections(rawSections, portal, options)
}
